'use client';

import { ChangeEvent, FormEvent, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { INewsMeta } from '@/models/INewsMeta';
import { twMerge } from 'tailwind-merge';
// import MenuArrows from '../components/svg/MenuArrows';
import DatePickerComp from '../components/DatePicker';
import Close from '../components/svg/Close';
// import ArticleBrief from '../components/ArticleBrief';
import { useDebounce } from '../../hooks/useDebounce';
import { apiService } from '../../services/ApiService';
import { clearQueryParams, deleteQueryParams, getQueryParam, setQueryParam, setQueryParams } from '../../utils/querystring';
import { getQueryFormattedDate } from '../../utils/date';
import { ICommonCategoryMeta, IPost, IPostFilter } from '../../models/ICategory';
import Link from 'next/link';
import { isBrowser } from '../../utils/isBrowser';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { IPaginatedData } from '../../models/ICommon';
// import Button, { ButtonTheme } from '../components/Button';
// import DropdownMenu from '../components/DropdownMenu';
import Search from '../components/svg/Search';
import Loader from '../components/svg/Loader';
import Arrow from '../components/svg/Arrow';
import CheckboxSelected from '../components/svg/CheckboxSelected';
import CheckboxUnselected from '../components/svg/CheckboxUnselected';
import MenuArrows from '../components/svg/MenuArrows';
import DropdownMenu from '../components/DropdownMenu';
import Post from '../components/Post';
import LoadMoreButton from '../components/LoadMoreButton';
// import SearchText from '../components/svg/SearchText';

interface Props {
  initialData: {
    posts: IPaginatedData<IPost>;
  };
  initalFilters?: Partial<IPostFilter>;
  categories: ICommonCategoryMeta[];
  writers: {uniqueCode: string; name: string; isUser: boolean; selected?: boolean;}[];
  locales: {
    filters: string;
    clearFilters: string;
    writer: string;
    title: string;
    total: string;
    search: string;
    category: string;
    date: {
      from :string;
      to: string;
    };
    loading: string;
    button: string;
  };
  locale: string;
}
export default function Filters({ 
  initialData, 
  locales, 
  categories, 
  writers, 
  initalFilters, 
  locale,
}: Props) {
  const inputRef = useRef<any>();


  const initialState = {
    //@ts-ignore
    dirty: initalFilters ? Object.keys(initalFilters).filter(x => x !== 'q').some(x => initalFilters[x]) : false,
    raw: initialData.posts,
    total: initialData.posts.data.length,
    displayedPosts: initialData.posts.data || [],
    isLoading: false,
    search: initalFilters?.q || '',
    filtersCount: 0,
    searchResult: initalFilters?.q || '',
    expandFilters: false,
    filters: {
      q: initalFilters?.q || '',
      category: initalFilters?.category ? (Array.isArray(initalFilters.category) ? initalFilters.category : [initalFilters.category]) : [],
      fromDate: initalFilters?.fromDate as Date,
      toDate: initalFilters?.toDate as Date,
      writer: initalFilters?.writer || ''
    }
  }




  const [state, setState] = useState<
    {
      dirty: boolean,
      raw: IPaginatedData<IPost>;
      displayedPosts: IPost[];
      isLoading: boolean;
      search: string;
      filtersCount: number;
      searchResult: string;
      expandFilters: boolean;
      filters: {
        q: string;
        category?: string[];
        writer?: string;
        fromDate: Date | undefined;
        toDate: Date | undefined;
      }
    }
  >(initialState);


  const toggleFiltersExpansion = () => setState(prevState => ({...prevState, expandFilters: !prevState.expandFilters}))

  const debouncedKeyword = useDebounce<string>(state.filters.q);

  const queryClient = useQueryClient();

  const queryKey = [locale, 'search'];


  const {
    data,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage
  } = useInfiniteQuery({
    queryKey,
    retry: false,
    enabled: false,
    initialData: () => {
      return {
        pageParams: [undefined, 1],
        pages: [initialData.posts]
      }
    },
    queryFn: async ({ pageParam = 1 }) => {
      const result = await apiService.searchPosts({ ...state.filters!, q: debouncedKeyword }, { page: pageParam as number, pageSize: initialData.posts.pageSize });
      return result;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage) return 1;
      return lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined;
    },
    initialPageParam: 1
  });

  
  const totalData = useMemo(() => {
    const allPosts = (data?.pages?.map(p => p.data).flat(Infinity) || []) as IPost[];
    return Array.from(
      new Map(allPosts.map(post => [post.uniqueCode, post])).values()
    );
  }, [data]) as IPost[];
  

  useEffect(() => {
    const q = getQueryParam('q');
    const category = getQueryParam('category', true) as string[];
    const writer = getQueryParam('writer');
    const fromDate = getQueryParam('fromDate') as string;
    const toDate = getQueryParam('toDate') as string;

    const keys = ['q', 'category', 'fromDate', 'toDate'];

    const params = keys.map(k => getQueryParam(k)).filter(x => x);
    if (!params.length) {
      deleteQueryParams(keys);
      return;
    }

    const normalizedCategory = category ? (Array.isArray(category) ? category : [category]) : undefined;
    const selectedFiltersCount = (normalizedCategory ? normalizedCategory.length : 0) + (fromDate ? 1 : 0) + (toDate ? 1 : 0);
      setState(prevState => ({
        ...prevState,
        filtersCount: selectedFiltersCount,
        filters: {
          ...prevState.filters,
          ...(q ? {q} : {}),
          ...(writer ? {writer} : {}),
          ...(normalizedCategory ? {category: normalizedCategory} : {}),
          ...(fromDate ? {fromDate: getQueryFormattedDate(fromDate)} : {}) as any,
          ...(toDate ? {toDate: getQueryFormattedDate(toDate)} : {}) as any,
        }
      }));


      return () => {
        queryClient.removeQueries({queryKey});
      }
    
  },[]);



  const handleClearFilters = async () => {

    let result: IPaginatedData<IPost> = {data: [],page: 1, pageSize: 10,totalPages: 0, totalRecords: 0};

    if (state.search) {
      result = await apiService.searchPosts({q: state.search}, {page: 1, pageSize: 12});
    }
    setState(prevState => ({
      ...prevState,
      dirty: false,
      displayedPosts: result.data,
      filtersCount: 0,
      filters: {
        ...prevState.filters,
        fromDate: undefined,
        toDate: undefined,
        category: [],
        writer: undefined
      },
      isLoading:false,
      raw: result
    }));


    // clear filters keep q and fetch posts if q exists
    // if no q exist and you apply filter, dont show result
    const params = ['fromDate', 'toDate','category'];
    deleteQueryParams(params);

    // if (state.filters.q) {
    //   const result = await apiService.searchPosts({}, {page: 1, pageSize: 10});
    //   setState(prevState => ({filters: {}}))

    // } else {
      queryClient.setQueryData(queryKey, () => ({
        pages: [result],
        pageParams: [undefined, 1],
      }));
    // }

  }


  const handleFilterChange = async (key: keyof typeof state['filters'], value: any) => {
    setState(prevState => { 
      //@ts-ignore
      const prevValue = Array.isArray(prevState.filters[key]) ? prevState.filters[key].length : (prevState.filters[key] ? 1 : 0);
      const newValue = Array.isArray(value) ? value.length : (value ? 1 : 0);
      const difference = newValue - prevValue;
      return {...prevState, dirty: true, filters: { ...prevState.filters, [key]: value }, isLoading: Boolean(state.filters.q), filtersCount: prevState.filtersCount + difference} 
    }
    );
    if (Array.isArray(value)) setQueryParams({[key]: value});
    else setQueryParam({[key]: value});
    if (!state.filters.q) return;
    const result = await apiService.searchPosts({ ...state.filters, [key]: value });
    queryClient.setQueryData(queryKey, (oldData: IPaginatedData<IPost>) => {
      return {
        pages: [{ page: 1, pageSize: result.pageSize, totalPages: result.totalPages, totalRecords: result.totalRecords, data: result.data }],
        pageParams: [undefined, 1],
      }
    })
    setState(prevState => ({ ...prevState, displayedPosts: result.data, raw: result, isLoading: false }));
  }

  const handleDate = async (field: 'fromDate' | 'toDate', date: Date) => {
    setQueryParam({ [field]: getQueryFormattedDate(date)});


    setState(prevState => ({
      ...prevState,
      dirty: true,
      isLoading: true,
      filtersCount: prevState.filtersCount + (prevState.filters[field] ? 0 : 1),
      filters: {
        ...prevState.filters,
        [field]: date
      }
    }));

    const result = await apiService.searchPosts({ ...state.filters, [field]: date });
    setState(prevState => ({ ...prevState, displayedPosts: result.data, isLoading: false, raw: result }));
    queryClient.setQueryData(queryKey, (oldData: IPaginatedData<IPost>) => {
      return {
        pages: [{ page: 1, pageSize: result.pageSize, totalPages: result.totalPages, totalRecords: result.totalRecords, data: result.data }],
        pageParams: [undefined, 1],
      }
    })

  }

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    if (!state.search) return;
    
    setState(prevState => ({...prevState, isLoading: true, searchResult: state.search, filters: {...prevState.filters, q: state.search}}));
    const result = await apiService.searchPosts({...state.filters, q: state.search});
    queryClient.setQueryData(queryKey, (oldData: IPaginatedData<IPost>) => {
      return {
        pages: [{ page: 1, pageSize: result.pageSize, totalPages: result.totalPages, totalRecords: result.totalRecords, data: result.data }],
        pageParams: [undefined, 1],
      }
    });

    setState(prevState => ({...prevState, isLoading: false, displayedPosts: result.data, raw: result}));
  }


  return (
    <div className='flex flex-col w-full mx-auto max-w-[1296px]  px-[16px] desktop:px-0 pb-[24px] pt-[24px]'>
      <h1 className='hidden'>{locales.title}</h1>
      <div className='flex flex-col gap-y-[16px]'>
        <form onSubmit={handleSearch} className='flex items-center w-full rounded-[2.05vw] tablet:rounded-[1.33vw] desktop:rounded-[0.81vw] xl:rounded-[8px] h-[10.77vw] tablet:h-[8.67vw] desktop:h-[5.24vw] xl:h-[52px]'>
          <div className='w-full h-fit'>
            <div className='flex items-center w-full h-full'>
        
            <div className='h-[60px] w-full border-gray-0 dark:border-lightBorder bg-gray-0 dark:bg-quinary border-[1.5px] p-[16px] flex items-center justify-between gap-x-[24px] rounded-[6px]'>
        <input 
       suppressContentEditableWarning
       ref={inputRef}
       autoComplete='off'
       className='outline-none border-none bg-transparent w-full h-full text-black dark:text-white h5'
       placeholder={locales.search}
       value={state.search}
       name='q'
       onChange={({ target: { value } }) => {
         setState(prevState => ({...prevState, search: value}));
         setQueryParam({q: value});
       }}
       type='text'
          />
          <button className='w-fit h-full grid place-items-center' type='submit'>
        <Search className='aspect-square w-[17px] flex-shrink-0'/>
          </button>
      </div>

          {/* <SearchCount className='hidden tablet:flex' /> */}
          <input disabled={state.isLoading || isLoading || isFetching} type="submit" className='hidden z-[-100]' tabIndex={-1} />
      </div>
      </div>
        </form>
        <div className='grid grid-cols-1 desktop:grid-cols-[1fr_306px] desktop:gap-[24px]'>
          <SearchResult className='desktop:hidden' count={totalData.length} filtersCount={state.filtersCount} title={state.searchResult} toggleExpand={toggleFiltersExpansion}/>
              <div className='block desktop:hidden w-full h-[1px] bg-gray-0 dark:bg-lightBorder flex-shrink-0 my-[24px]'/>
          <div className='desktop:flex flex-col gap-y-[24px] hidden overflow-x-hidden'>
            <SearchResult count={totalData.length} filtersCount={state.filtersCount} title={state.searchResult} toggleExpand={toggleFiltersExpansion}/>
              <div className='hidden desktop:block w-full h-[1px] bg-gray-0 dark:bg-lightBorder flex-shrink-0'/>
            <PostsContiner 
              fetchNextPage={fetchNextPage} 
              hasNextPage={hasNextPage} 
              initialData={initialData} 
              locale={locale} 
              locales={locales} 
              isFetching={isFetching} 
              isLoading={isLoading} 
              state={state}
              totalData={totalData}
            />
          </div>
          <SubFilters
            categories={categories} 
            writers={writers} 
            handleClearFilters={handleClearFilters} 
            handleDate={handleDate} 
            handleFilterChange={handleFilterChange} 
            locales={locales}
            state={state}
            setState={setState}
            />

         

            <PostsContiner 
            className='flex desktop:hidden'
            fetchNextPage={fetchNextPage} 
            hasNextPage={hasNextPage} 
            initialData={initialData} 
            locale={locale} 
            locales={locales} 
            isFetching={isFetching} 
            isLoading={isLoading} 
            state={state}
            totalData={totalData}
            />

        </div>
      </div>

    
    </div>
  )
}



interface SearchResultProps {
  title: string;
  count: number;
  filtersCount: number;
  toggleExpand: Function;
  className?: string;
}

const SearchResult = ({  count,filtersCount,title, toggleExpand, className }: SearchResultProps) => {
  return (
    <div className={twMerge('flex items-center h-fit justify-between pt-[12px] tablet:pt-0', className)}>
      {title ? 
      <div className='flex items-center flex-wrap gap-[8px]'>
        <h2 className='h3'>{title}</h2>
        <span className='sm text-gray-600 dark:text-gray-500 whitespace-nowrap'>(عرض {count} نتيجة من كل المحتوى)</span>
      </div> : null}

      <div 
        className='flex items-center justify-between gap-[10px] cursor-pointer w-fit h-[40px] p-[10px] border-[1px] border-gray-0 dark:border-lightBorder rounded-[4px]'
        onClick={() => toggleExpand()}
        >
        <span className='md font-medium'>تصفية</span>
        <div className='h-[20px] min-w-[20px] grid w-fit px-[3px] place-items-center text-white bg-primary dark:bg-secondary rounded-full sm font-bold'>{filtersCount}</div>
      </div>
    </div>
  );
}

interface SubFiltersProps {
  writers: any[];
  categories: any[];
  state: any;
  setState: any;
  handleClearFilters: Function;
  handleFilterChange: any;
  handleDate: any;
  locales: any;
  className?: string;
}

function SubFilters({className, categories, locales, handleClearFilters, handleFilterChange, handleDate, state, setState, writers}: SubFiltersProps) {

  const [innerState, setInnerState] = useState(() => {

    const displayedWriters = writers.slice(0,6);
    const displayedCategories = categories.slice(0,6);
    return {
    writers: {
      expanded: false,
      loadMore: displayedWriters.length < writers.length,
      displayed: displayedWriters
    },
    categories: {
      expanded: false,
      loadMore: displayedCategories.length < categories.length,
      displayed: displayedCategories
    },
    dates: {
      expanded: false
    }
  }
  })



  const handleCategorySelection = (category: any, selected: boolean) => {
    const newState = structuredClone(state.filters.category)! as any[];
    if (selected) {
      const index = newState.findIndex((i: string) => i === category.uniqueCode);
      newState.splice(index,1);
    } else {
      newState.push(category.uniqueCode);
    }
    handleFilterChange('category', newState);
  }

  if (!state.expandFilters) return <div/>;

  return (
<div className={twMerge('flex flex-col p-[24px] border-[1px] h-fit border-gray-0 dark:border-lightBorder min-w-[306px] mb-[24px] desktop:mb-0', className)}>

<div className='flex items-center justify-between mb-[24px]'>
<span className='h3 tablet:font-bold'>{locales.filters}</span>
<span 
onClick={() => state.dirty ? handleClearFilters() : undefined} 
className={twMerge('w-fit cursor-pointer md text-primary dark:text-secondary', !state.dirty && 'hidden')}
>
{locales.clearFilters}
</span>
</div>


<div className='flex flex-col'>
  <div className='flex flex-col w-full'>
    <div className='flex items-center justify-between w-full cursor-pointer pt-[8px] mb-[16px] border-t-[1px] border-t-gray-0 dark:border-t-lightBorder' onClick={() => setInnerState(prevState => ({...prevState, writers: {...prevState.writers, expanded: !prevState.writers.expanded}}))}>
      <span className=' lg'>الكاتب</span>
      <Arrow className={twMerge('h-[10px] w-[10px] [&>*]:fill-dark dark:[&>*]:fill-white transition-all duration-300', innerState.writers.expanded && 'rotate-[180deg]')}/>
    </div>
    {innerState.writers.expanded ? (
      <div className='flex flex-col w-full gap-y-[12px] mb-[12px] h-fit max-h-[350px] overflow-y-auto'>
        <DropdownMenu
        className={{item: 'text-black'}}
          onChange={(value: any) => handleFilterChange('writer', value as any)}
  options={innerState.writers.displayed.map(x => ({ title: x.name, value: x.uniqueCode }))}
  selected={state.filters.writer}
  // className={{ wrapper: 'select max-w-[50%] tablet:max-w-none' }}
        />
                {innerState.writers.loadMore ? <span onClick={() => setInnerState(prevState => ({...prevState, writers: {...prevState.writers, loadMore: false, displayed: writers }}))} className='sm text-third text-primary dark:text-secondary cursor-pointer'>المزيد ({writers.length - innerState.writers.displayed.length})</span> : null}
      </div>
    ) : null}
  </div>
  <div className='flex flex-col w-full'>
    <div className='flex items-center justify-between w-full cursor-pointer pt-[8px] mb-[16px] border-t-[1px] border-t-gray-0 dark:border-t-lightBorder' onClick={() => setInnerState(prevState => ({...prevState, categories: {...prevState.categories, expanded: !prevState.categories.expanded}}))}>
      <span className=' lg'>القسم</span>
      <Arrow className={twMerge('h-[10px] w-[10px] [&>*]:fill-dark dark:[&>*]:fill-white transition-all duration-300', innerState.categories.expanded && 'rotate-[180deg]')}/>
    </div>
    {innerState.categories.expanded ? (
      <div className='flex flex-col w-full gap-y-[12px] mb-[12px] h-fit max-h-[350px] overflow-y-auto'>
        {innerState.categories.displayed.map((c,i) => {
          const selected = state.filters.category?.includes(c.uniqueCode);
          return (
          <div 
          className='flex items-center justify-between w-full pl-[24px]' 
          key={c.uniqueCode} 
            >
            <span className='sm  cursor-pointer' onClick={() => handleCategorySelection(c, selected)}>{c.title}</span>  
            <div className='cursor-pointer grid place-items-center' onClick={() => handleCategorySelection(c,selected)}>
              {state.filters.category?.includes(c.uniqueCode) ? <CheckboxSelected /> : <CheckboxUnselected/>}
              </div>
          </div>
        )})}
        {innerState.categories.loadMore ? <span onClick={() => setInnerState(prevState => ({...prevState, categories: {...prevState.categories, loadMore: false, displayed: categories }}))} className='sm text-third text-primary dark:text-secondary cursor-pointer'>المزيد ({categories.length - innerState.categories.displayed.length})</span> : null}
      </div>
    ) : null}
  </div>

  <div className='flex flex-col w-full'>
    <div className='flex items-center justify-between w-full cursor-pointer pt-[8px] mb-[16px] border-t-[1px] border-t-gray-0 dark:border-t-lightBorder' onClick={() => setInnerState(prevState => ({...prevState, dates: {expanded: !prevState.dates.expanded}}))}>
      <span className=' lg'>التاريخ</span>
      <Arrow className={twMerge('h-[10px] w-[10px] [&>*]:fill-dark dark:[&>*]:fill-white transition-all duration-300', innerState.dates.expanded && 'rotate-[180deg]')}/>
    </div>
    {innerState.dates.expanded ? (
      <div className='grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-1 w-full gap-[12px] '>
        <DatePickerComp placeholder={locales.date.from} field='fromDate' state={{ fromDate: state.filters.fromDate, toDate: state.filters.toDate }} handleDate={handleDate} />
        <DatePickerComp placeholder={locales.date.to} field='toDate' state={{ fromDate: state.filters.fromDate, toDate: state.filters.toDate }} handleDate={handleDate} />
      </div>
    ) : null}
  </div>
</div>

</div>
  )
}
interface MenuSelectProps {
  selected?: string | number;
  placeholder?: string;
  onChange: (value: string | number) => void;
  options: {
    title: string;
    value: string | number;
  }[];
  dir?: 'ltr' | 'rtl';
  className?: Partial<{
    wrapper: string;
    menu: Partial<{
      wrapper: string;
      item: string;
    }>;
  }>;
}

export function MenuSelect({ selected, placeholder, onChange, className, options, dir = 'rtl' }: MenuSelectProps) {

  const [expanded, setExpanded] = useState(false);


  const menuRef = useRef<any>();
  const containerRef = useRef<any>();

  const handleClick = (e: MouseEvent) => {
    const { current: menu } = menuRef;
    const { current: container } = containerRef;
    if (
      (container?.contains(e.target) || container?.contains(menu)) &&
      (menu ? !menu.contains(e.target) : true)
    ) setExpanded(prevState => !prevState);
  }

  useEffect(() => {
    window.addEventListener('mousedown', handleClick);
    return () => {
      window.removeEventListener('mousedown', handleClick);
    }
  }, []);


  const handleChange = (value: string | number) => {
    onChange(value);
    setExpanded(false);
  }

  return (
    <div ref={containerRef} dir={dir} className={twMerge(className?.wrapper)}>
      <div className='flex items-center h-[36px] gap-[inherit] overflow-hidden w-full justify-between [&>*]:flex-shrink-0 bg-white dark:bg-dark p-[9px] cursor-pointer'>

      <span className={twMerge(' w-[80%] overflow-hidden md')}>{options.find(({ value }) => selected === value)?.title || placeholder || ''}</span>
      <MenuArrows className='h-fill w-[30px]' />
      </div>
      {expanded ? <DropdownMenu ref={menuRef} key={String(expanded)} selected={selected} onChange={handleChange} options={options} className={className?.menu} /> : null}
    </div>
  )
}



interface PostsContainerProps {
state: any;
totalData: IPost[];
initialData: {posts: IPaginatedData<IPost>};
className?: string;
locale: string;
locales: any;
isLoading: boolean;
isFetching: boolean;
hasNextPage: boolean;
fetchNextPage: Function;
}


  function PostsContiner({className,initialData,state,totalData, locale, hasNextPage,isFetching,isLoading,locales, fetchNextPage}: PostsContainerProps) {


    return (
      <div className={twMerge('flex flex-col gap-y-[24px] pb-[24px]', className)}>

      <div className='grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-3 gap-[24px]'>
      {
        state.isLoading ? null  :
        totalData.length ? totalData.map((p, i) => {
          return (
              <Post 
               badge={{mobile: 'image', tablet: 'image', desktop: 'image'}}
          newsType={p.newsType}
        key={p.uniqueCode}
        type={p.type}
          title={p.title} 
          category={p.category} 
          classNames={{
            wrapper: 'rounded-[12px] h-full',
            contentContainer: 'p-[16px] flex-1 tablet:justify-start',
            image: 'aspect-[4/3] mb-0',
            description: 'line-clamp-3 tablet:line-clamp-1',
            category: '[&>*]:h6 mb-[12px] tablet:mb-[8px]',
            title: 'line-clamp-3 h5'
          }} 
          image={{src: p.imageUrls.url, fill: true, alt: p.title, sizes: '100vw', className: 'rounded-tr-[12px] rounded-tl-[12px]', loading: i === 0 ? 'eager' : 'lazy', priority: i === 0}} 
          uniqueCode={p.uniqueCode} 
          thumbnail={p.imageUrls.thumbUrl}
          />
            )
          }
          ) : <div className='grid place-items-center w-full  lg font-semibold whitespace-nowrap'>المعذرة لا يوجد نتائج</div>}
    </div>
    {hasNextPage ? 
    <LoadMoreButton isLoading={isLoading || isFetching || state.isLoading } onClick={fetchNextPage}/> 
    : null}
          </div>
    )
  }

